import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { UserManagementFormContext } from 'features/user/contexts/UserManagementFormContext'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { ROUTES } from 'routes'
import { Schema } from '../../schema'
import { CREATE_PUBLISHER_BY_EMPLOYEE, Data, Variables } from './api'
import { mapper } from './mapper'
import { UseCreatePublisherByEmployee } from './types'

export const useCreatePublisherByEmployee = (): UseCreatePublisherByEmployee => {
  const navigate = useNavigate()
  const { t } = useTranslation('features/user')
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})
  const { setLoading } = useContext(UserManagementFormContext)

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: CREATE_PUBLISHER_BY_EMPLOYEE,
    onCompleted: async (data: Data): Promise<void> => {
      const {
        createPublisherByEmployee: { errors, user },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.publisher.add.error'), { variant: 'error' })
      } else if (user) {
        setErrors({})

        navigate(generatePath(ROUTES.USER.LIST, { tab: 'publishers' }))
        enqueueSnackbar(t('form.publisher.add.success'), { variant: 'success' })
      }
    },
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const createPublisherByEmployee = (data: Schema): void => {
    void mutateAsync({
      input: mapper(data),
    })
  }

  return { createPublisherByEmployee, errors }
}
